import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.learn.taegener.info/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token interceptor
let getToken: (() => string | null) | null = null;

export function setTokenGetter(getter: () => string | null) {
  getToken = getter;
}

api.interceptors.request.use((config) => {
  if (getToken) {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Types
export interface ProgressSummary {
  totalPoints: number;
  level: number;
  levelName: string;
  completedCourses: number;
  totalCourses: number;
  currentStreak: number;
  isActiveToday: boolean;
  nextMilestone: {
    type: string;
    target: number;
    reward: string;
  } | null;
}

export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  lastActivityAt: string | null;
  isActiveToday: boolean;
  nextMilestone: number | null;
  pointsToNextMilestone: number;
}

export interface Badge {
  id: string;
  slug: string;
  name: string;
  description: string;
  imageUrl: string | null;
  badgeType: string;
  pointsValue: number;
  earned: boolean;
  earnedAt: string | null;
}

export interface Course {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  thumbnailUrl: string | null;
  pointsReward: number;
  estimatedDurationMinutes: number | null;
}

export interface Recommendation {
  id: string;
  title: string;
  description: string | null;
  level: string;
  points: number;
  reason: string;
  deepLink: string;
  thumbnailUrl: string | null;
  estimatedDuration: number | null;
}

export interface CertificateVerification {
  valid: boolean;
  expired: boolean;
  certificate: {
    name: string;
    courseName: string;
    recipientName: string;
    issuedAt: string;
    expiresAt: string | null;
    verificationCode: string;
  } | null;
}

// API Functions
export const userApi = {
  getDashboard: () => api.get('/me/dashboard'),
  getProfile: () => api.get('/me/profile'),
  updateProfile: (data: Record<string, unknown>) => api.put('/me/profile', data),
  getProgressSummary: () => api.get<ProgressSummary>('/me/progress/summary'),
  getStreak: () => api.get<StreakInfo>('/me/streak'),
  recordActivity: () => api.post('/me/activity'),
  getBadges: () => api.get<Badge[]>('/me/badges'),
  getAllBadges: () => api.get<Badge[]>('/me/badges/all'),
  getRecommendations: (limit = 3) => api.get<{ recommendations: Recommendation[] }>(`/me/recommendations?limit=${limit}`),
  getLeaderboard: (limit = 10) => api.get(`/me/leaderboard?limit=${limit}`),
  getEnrollments: () => api.get('/me/enrollments'),
  getCertificates: () => api.get('/me/certificates'),
  getPoints: () => api.get('/me/points'),
};

export interface Category {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  _count: { courses: number };
}

export interface LessonDetails {
  id: string;
  slug: string;
  title: string;
  contentType: string;
  contentUrl: string | null;
  contentText: string | null;
  estimatedDurationMinutes: number | null;
  pointsReward: number;
  course: { slug: string; title: string };
}

export interface LessonCompletionResult {
  success: boolean;
  lessonCompleted: boolean;
  courseCompleted: boolean;
  pointsAwarded: number;
  totalPoints: number;
  enrollment: {
    status: string;
    progressPercent: number;
  };
  badgesEarned?: string[];
}

export interface MyCourse {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  pointsReward: number;
  thumbnailUrl: string | null;
  workArea: { id: string; slug: string; name: string } | null;
  skillLevel: { id: string; slug: string; name: string } | null;
  _count: { modules: number };
  enrollment: {
    id: string;
    status: string;
    progressPercent: number;
    startedAt: string | null;
    completedAt: string | null;
    lastAccessedAt: string | null;
  };
}

export interface SourceApp {
  id: string;
  name: string;
  slug: string;
}

export interface CoursesResponse {
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  sourceApp?: SourceApp | null;
}

export const courseApi = {
  getCategories: () => api.get<Category[]>('/courses/categories'),
  getCourses: (params?: { categorySlug?: string; source?: string }) =>
    api.get<CoursesResponse>('/courses', {
      params: {
        ...(params?.categorySlug ? { workArea: params.categorySlug } : {}),
        ...(params?.source ? { source: params.source } : {}),
      },
    }),
  getCourse: (slug: string) => api.get(`/courses/${slug}`),
  enrollCourse: (slug: string) => api.post(`/courses/${slug}/enroll`),
  getModules: (slug: string) => api.get(`/courses/${slug}/modules`),
  getLesson: (courseSlug: string, lessonSlug: string) =>
    api.get<LessonDetails>(`/courses/${courseSlug}/lessons/${lessonSlug}`),
  completeLesson: (courseSlug: string, lessonSlug: string, data?: { score?: number; timeSpentSeconds?: number }) =>
    api.post<LessonCompletionResult>(`/courses/${courseSlug}/lessons/${lessonSlug}/complete`, data || {}),
  getMyCourses: (status?: string) =>
    api.get<{ data: MyCourse[] }>('/courses/my-courses', { params: status ? { status } : {} }),
};

// Admin API
export interface UserProfile {
  id: string;
  keycloakUserId: string;
  displayName: string | null;
  email: string | null;
}

export interface Assignment {
  id: string;
  userId: string;
  courseId: string;
  status: string;
  enrolledAt: string;
  user: UserProfile;
}

export interface ModuleAssignments {
  module: { id: string; title: string; slug: string };
  assignments: Assignment[];
}

export const adminApi = {
  // Users
  getUsers: () => api.get<UserProfile[]>('/admin/users'),

  // Module Assignments
  getModuleAssignments: (courseId: string) =>
    api.get<ModuleAssignments>(`/admin/courses/${courseId}/assignments`),
  assignUsers: (courseId: string, userIds: string[]) =>
    api.post(`/admin/courses/${courseId}/assign`, { userIds }),
  removeAssignment: (courseId: string, userId: string) =>
    api.delete(`/admin/courses/${courseId}/assign/${userId}`),

  // Courses for admin
  getCourses: () => api.get('/admin/courses'),
  updateCourse: (id: string, data: { title?: string; slug?: string; description?: string; isPublished?: boolean }) =>
    api.put(`/admin/courses/${id}`, data),
  changeCourseUuid: (currentId: string, newId: string) =>
    api.put(`/admin/courses/${currentId}/change-uuid`, { newId }),
};

export const certificateApi = {
  verify: (code: string) => api.get<CertificateVerification>(`/certificates/verify/${code}`),
  download: (id: string) => api.get(`/certificates/${id}/download`, { responseType: 'blob' }),
};

// Lesson lookup for deep-linking
export interface LessonLookup {
  id: string;
  slug: string;
  title: string;
  contentType: string;
  estimatedDurationMinutes: number | null;
  pointsReward: number;
  course: {
    id: string;
    slug: string;
    title: string;
  };
  deepLink: string;
}

export const lessonApi = {
  getBySlug: (slug: string) => api.get<LessonLookup>(`/lessons/${slug}`),
};

// Quiz Types
export interface QuizAnswer {
  id: string;
  answerText: string;
  sortOrder: number;
}

export interface QuizQuestion {
  id: string;
  questionType: 'single-choice' | 'multiple-choice' | 'true-false' | 'free-text';
  questionText: string;
  explanation?: string;
  points: number;
  sortOrder: number;
  answers: QuizAnswer[];
}

export interface Quiz {
  id: string;
  title: string;
  description?: string;
  quizType: string;
  passingScorePercent: number;
  timeLimitMinutes?: number;
  maxAttempts?: number;
  shuffleQuestions: boolean;
  shuffleAnswers: boolean;
  showCorrectAnswers: boolean;
  pointsReward: number;
  questions: QuizQuestion[];
  module?: { id: string; title: string; course: { id: string; slug: string; title: string } };
  course?: { id: string; slug: string; title: string };
}

export interface QuizAttempt {
  id: string;
  attemptNumber: number;
  scorePercent?: number;
  passed?: boolean;
  startedAt: string;
  completedAt?: string;
  timeSpentSeconds?: number;
  answers?: Record<string, string[]>;
}

export interface QuizQuestionResult {
  questionId: string;
  correct: boolean;
  userAnswers: string[];
  correctAnswers?: string[];
  explanation?: string;
}

export interface QuizSubmitResult {
  attempt: QuizAttempt;
  scorePercent: number;
  passed: boolean;
  passingScore: number;
  pointsEarned: number;
  results: QuizQuestionResult[];
}

export const quizApi = {
  getQuiz: (id: string) => api.get<Quiz>(`/quizzes/${id}`),
  startAttempt: (id: string) => api.post<QuizAttempt>(`/quizzes/${id}/start`),
  submitQuiz: (id: string, attemptId: string, answers: Record<string, string[]>) =>
    api.post<QuizSubmitResult>(`/quizzes/${id}/submit`, { attemptId, answers }),
  getResults: (id: string, attemptId: string) =>
    api.get<QuizAttempt & { quiz: Quiz }>(`/quizzes/${id}/results/${attemptId}`),
};

// Release Notes Types
export interface ReleaseNote {
  id: string;
  version: string;
  title: string;
  content: string;
  releaseType: 'major' | 'minor' | 'patch';
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
}

// Release Notes API (Public)
export const releaseNotesApi = {
  getAll: (limit = 10) => api.get<ReleaseNote[]>(`/system/release-notes?limit=${limit}`),
  getLatest: () => api.get<ReleaseNote>('/system/release-notes/latest'),
  getById: (id: string) => api.get<ReleaseNote>(`/system/release-notes/${id}`),
};

// Admin Release Notes API
export const adminReleaseNotesApi = {
  getAll: () => api.get<ReleaseNote[]>('/admin/release-notes'),
  create: (data: { version: string; title: string; content: string; releaseType: string }) =>
    api.post<ReleaseNote>('/admin/release-notes', data),
  update: (id: string, data: Partial<{ version: string; title: string; content: string; releaseType: string }>) =>
    api.put<ReleaseNote>(`/admin/release-notes/${id}`, data),
  delete: (id: string) => api.delete(`/admin/release-notes/${id}`),
  publish: (id: string) => api.post<ReleaseNote>(`/admin/release-notes/${id}/publish`),
};

export default api;
